import styled from 'styled-components';
import colors from '../../assets/styles/variables/colors';

export const Page = styled.div`
  padding-top: 80px;
  min-height: 100vh;
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 24px 40px;
  padding: 10px 20px;
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 10px;
  color: ${colors.text.light.medium};
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    border-color: ${colors.borderHover};
    color: ${colors.text.light.very};
  }
`;

export const DetailHero = styled.section`
  padding: 60px 40px 80px;
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 200px;
    background: ${(props) => props.glowColor};
    border-radius: 50%;
    filter: blur(100px);
    pointer-events: none;
  }
`;

export const HeroBadge = styled.div`
  display: inline-block;
  padding: 8px 20px;
  border-radius: 100px;
  background: ${(props) => props.accentColor}12;
  border: 1px solid ${(props) => props.accentColor}30;
  color: ${(props) => props.accentColor};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 28px;
`;

export const HeroTitle = styled.h1`
  font-size: 56px;
  font-weight: 900;
  color: ${colors.text.light.very};
  letter-spacing: -2.5px;
  line-height: 1.1;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

export const HeroTagline = styled.p`
  font-size: 18px;
  color: ${colors.text.light.medium};
  max-width: 560px;
  margin: 0 auto;
  line-height: 1.6;
`;

export const OverviewSection = styled.section`
  padding: 80px 40px;
  max-width: 1200px;
  margin: 0 auto;
`;

export const SectionHeader = styled.div`
  margin-bottom: 40px;
`;

export const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.text.light.very};
  letter-spacing: -1px;
  margin-bottom: 12px;
`;

export const SectionDescription = styled.p`
  font-size: 15px;
  color: ${colors.text.light.little};
  line-height: 1.7;
  max-width: 600px;
`;

export const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const OverviewCard = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  padding: 32px;
  ${(props) => props.full && 'grid-column: 1 / -1;'}
`;

export const OverviewLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.accent};
  margin-bottom: 16px;
`;

export const OverviewText = styled.p`
  font-size: 15px;
  color: ${colors.text.light.medium};
  line-height: 1.8;
`;

export const AiSection = styled.section`
  padding: 80px 40px;
  max-width: 1200px;
  margin: 0 auto;
  border-top: 1px solid ${colors.border};
`;

export const AiGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const AiCard = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  padding: 32px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${(props) => props.accentColor}40;
  }
`;

export const AiCardTitle = styled.h4`
  font-size: 17px;
  font-weight: 700;
  color: ${colors.text.light.very};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;

  span {
    font-size: 13px;
    font-weight: 700;
    color: ${(props) => props.accentColor};
    font-family: 'JetBrains Mono', monospace;
    padding: 4px 10px;
    background: ${(props) => props.accentColor}12;
    border-radius: 6px;
  }
`;

export const AiCardDetail = styled.p`
  font-size: 14px;
  color: ${colors.text.light.little};
  line-height: 1.8;
  margin-bottom: 16px;
`;

export const AiTechHint = styled.div`
  padding: 12px 16px;
  background: ${(props) => props.accentColor}08;
  border: 1px solid ${(props) => props.accentColor}15;
  border-radius: 10px;
  font-size: 13px;
  color: ${(props) => props.accentColor};
  line-height: 1.6;
  font-family: 'JetBrains Mono', monospace;
`;

export const IntegrationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const IntegrationCard = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  padding: 36px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${colors.borderHover};
  }
`;

export const IntCategory = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${colors.accent};
  margin-bottom: 8px;
`;

export const IntName = styled.h3`
  font-size: 22px;
  font-weight: 800;
  color: ${colors.text.light.very};
  letter-spacing: -0.5px;
  margin-bottom: 16px;
`;

export const IntDescription = styled.p`
  font-size: 15px;
  color: ${colors.text.light.medium};
  line-height: 1.8;
  margin-bottom: 24px;
`;

export const IntFreeLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${colors.green};
  margin-bottom: 8px;
`;

export const IntFree = styled.p`
  font-size: 14px;
  color: ${colors.text.light.little};
  line-height: 1.7;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: ${colors.greenGlow};
  border-radius: 10px;
  border: 1px solid ${colors.green}15;
`;

export const IntUseCaseLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${colors.blue};
  margin-bottom: 8px;
`;

export const IntUseCase = styled.p`
  font-size: 14px;
  color: ${colors.text.light.little};
  line-height: 1.7;
  margin-bottom: 20px;
`;

export const IntLinksRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

export const IntSignup = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${colors.accentGlow};
  border: 1px solid ${colors.accent}25;
  border-radius: 8px;
  color: ${colors.accentLight};
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s;

  &:hover {
    background: ${colors.accent}25;
    border-color: ${colors.accent}40;
  }
`;

export const IntImportant = styled.div`
  padding: 14px 18px;
  background: ${colors.gold}0a;
  border: 1px solid ${colors.gold}25;
  border-radius: 10px;
  color: ${colors.gold};
  font-size: 13px;
  font-weight: 600;
  line-height: 1.6;
  margin-bottom: 20px;
`;

export const RequirementSection = styled.section`
  padding: 80px 40px;
  max-width: 1200px;
  margin: 0 auto;
  border-top: 1px solid ${colors.border};
`;

export const ReqGroup = styled.div`
  margin-bottom: 40px;
`;

export const ReqGroupTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.text.light.very};
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;

  span {
    font-size: 14px;
    font-weight: 700;
    color: ${colors.accent};
    font-family: 'JetBrains Mono', monospace;
    opacity: 0.5;
  }
`;

export const ReqList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-left: 24px;
`;

export const ReqItem = styled.li`
  font-size: 14px;
  color: ${colors.text.light.medium};
  line-height: 1.7;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${colors.accent}60;
  }
`;

export const EdgeSection = styled.section``;

export const EdgeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const EdgeCard = styled.div`
  background: ${colors.bgCard};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${(props) => props.accentColor}30;
  }
`;

export const EdgeNumber = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.accentColor}60;
  font-family: 'JetBrains Mono', monospace;
  flex-shrink: 0;
  padding-top: 2px;
`;

export const EdgeText = styled.p`
  font-size: 14px;
  color: ${colors.text.light.medium};
  line-height: 1.7;
`;
